<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, ref, type ComputedRef, type Ref } from "vue";
import { useRouter, type Router } from "vue-router";
import type { TenantActivationStatus, TenantSummary } from "../api/tenants.js";
import { listTiers, type ServiceTier } from "../api/system-tiers.js";
import AppShell from "../components/AppShell.vue";
import NavigationIcon from "../components/NavigationIcon.vue";
import TenantCreateForm from "../components/TenantCreateForm.vue";
import UserActionIcon from "../components/UserActionIcon.vue";
import { useTenantStore } from "../stores/tenants.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { canManageTenants } from "../api/system-authorization.js";
import { ApiRequestError } from "../api/http.js";
import { confirmDialog, promptDialog } from "../dialogs/app-dialog.js";

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const router: Router = useRouter();
const createFormVisible: Ref<boolean> = ref(false);
const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const tenantManagementAllowed: ComputedRef<boolean> = computed(
  (): boolean => canManageTenants(authenticationStore.session?.user.systemRoles ?? []),
);
const busyTenantId: Ref<string | null> = ref(null);
const errorMessage: Ref<string | null> = ref(null);
const tiers: Ref<ServiceTier[]> = ref([]);
const activeTiers: ComputedRef<readonly ServiceTier[]> = computed(
  (): readonly ServiceTier[] => tiers.value.filter((tier: ServiceTier): boolean => tier.status === "active"),
);

async function openMembers(tenant: TenantSummary): Promise<void> {
  const ownerUserId: string | null = tenantStore.ownerUserId;
  if (ownerUserId !== null) {
    tenantStore.selectTenant(tenant.id, ownerUserId);
  }
  await router.push({ name: "tenant-members", params: { tenantId: tenant.id } });
}

async function openCreatedTenant(tenant: TenantSummary): Promise<void> {
  createFormVisible.value = false;
  await router.push({ name: "tenant-overview", params: { tenantId: tenant.id } });
}

async function deleteSelectedTenant(tenant: TenantSummary): Promise<void> {
  const confirmation: string | null = await promptDialog({ title: "Permanently delete tenant?", message: "All probes, credentials, measurements, histories, memberships, invitations, and other server-side tenant data will be deleted.", inputLabel: `Type ${tenant.slug} to confirm`, requiredValue: tenant.slug, validationMessage: "The tenant slug does not match.", confirmLabel: "Delete tenant", tone: "danger" });
  if (confirmation === null) return;
  const userId: string | undefined = authenticationStore.session?.user.id;
  if (userId === undefined) return;
  busyTenantId.value = tenant.id;
  errorMessage.value = null;
  try { await tenantStore.removeTenant(tenant.id, userId); }
  catch (error: unknown) { errorMessage.value = error instanceof ApiRequestError ? error.message : "The tenant could not be deleted."; }
  finally { busyTenantId.value = null; }
}

async function toggleTenantStatus(tenant: TenantSummary): Promise<void> {
  const nextStatus: TenantActivationStatus = tenant.status === "disabled" ? "active" : "disabled";
  const action: string = nextStatus === "active" ? "enable" : "disable";
  const confirmed: boolean = await confirmDialog({ title: `${action[0]?.toUpperCase() ?? ""}${action.slice(1)} tenant?`, message: nextStatus === "disabled" ? `${tenant.displayName} will stop accepting tenant-user and probe access until it is enabled again.` : `${tenant.displayName} will become accessible again.`, confirmLabel: nextStatus === "disabled" ? "Disable tenant" : "Enable tenant", tone: nextStatus === "disabled" ? "danger" : "default" });
  if (!confirmed) return;
  busyTenantId.value = tenant.id;
  errorMessage.value = null;
  try { await tenantStore.setTenantStatus(tenant.id, nextStatus); }
  catch (error: unknown) { errorMessage.value = error instanceof ApiRequestError ? error.message : `The tenant could not be ${action}d.`; }
  finally { busyTenantId.value = null; }
}

async function renameTenant(tenant: TenantSummary): Promise<void> {
  const displayName: string | null = await promptDialog({
    title: "Rename tenant",
    message: "The tenant slug remains unchanged.",
    inputLabel: "Tenant name",
    initialValue: tenant.displayName,
    confirmLabel: "Rename tenant",
  });
  if (displayName === null || displayName.trim() === tenant.displayName) return;
  busyTenantId.value = tenant.id;
  errorMessage.value = null;
  try {
    await tenantStore.renameTenant(tenant.id, displayName);
  } catch (error: unknown) {
    errorMessage.value = error instanceof ApiRequestError ? error.message : "The tenant could not be renamed.";
  } finally {
    busyTenantId.value = null;
  }
}

async function loadTiers(): Promise<void> {
  try {
    tiers.value = [...await listTiers()];
  } catch (error: unknown) {
    errorMessage.value = error instanceof ApiRequestError ? error.message : "Tiers could not be loaded.";
  }
}

async function assignTier(tenant: TenantSummary, tierId: string): Promise<void> {
  if (tierId === tenant.tier.id) return;
  busyTenantId.value = tenant.id;
  errorMessage.value = null;
  try {
    await tenantStore.setTenantTier(tenant.id, tierId);
  } catch (error: unknown) {
    errorMessage.value = error instanceof ApiRequestError ? error.message : "The tier could not be assigned.";
  } finally {
    busyTenantId.value = null;
  }
}

void loadTiers();
</script>

<template>
  <AppShell>
    <header class="page-heading">
      <div>
        <p class="eyebrow">System administration</p>
        <h1>Tenants</h1>
        <p>All administrative and data-ownership boundaries on this installation.</p>
      </div>
      <button
        v-if="tenantManagementAllowed"
        class="button button--primary page-heading__action"
        type="button"
        :disabled="activeTiers.length === 0"
        @click="createFormVisible = !createFormVisible"
      >
        {{ createFormVisible ? "Cancel" : "Create tenant" }}
      </button>
    </header>

    <section v-if="tenantManagementAllowed && activeTiers.length === 0" class="content-panel tenant-create-section">
      <p class="eyebrow">Tier required</p>
      <h2>Create an active tier first</h2>
      <p>Tenants cannot be created without an explicit active tier assignment.</p>
      <RouterLink class="button button--primary" :to="{ name: 'system-tiers' }">Open tier configuration</RouterLink>
    </section>

    <section v-if="createFormVisible" class="content-panel tenant-create-section">
      <p class="eyebrow">New tenant</p>
      <h2>Create an isolated tenant</h2>
      <TenantCreateForm :tiers="activeTiers" @created="openCreatedTenant" />
    </section>

    <section class="content-panel data-table-panel">
      <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
      <div class="data-table data-table--tenants" role="table" aria-label="Tenants">
        <div class="data-table__header" role="row">
          <span role="columnheader">Tenant</span>
          <span role="columnheader">Status</span>
          <span role="columnheader">Tier</span>
          <span role="columnheader">Probes</span>
          <span role="columnheader">Actions</span>
        </div>
        <div v-for="tenant in tenantStore.tenants" :key="tenant.id" class="data-table__row" :class="{ 'data-table__row--inactive': tenant.status !== 'active' }" role="row">
          <div class="tenant-row__identity" role="cell">
            <strong>{{ tenant.displayName }}</strong>
            <span>{{ tenant.slug }}</span>
          </div>
          <span class="status-badge" :class="`status-badge--${tenant.status}`" role="cell">
            {{ tenant.status }}
          </span>
          <div role="cell">
            <select
              v-if="tenantManagementAllowed"
              class="table-select"
              :value="tenant.tier.id"
              :disabled="busyTenantId === tenant.id"
              @change="assignTier(tenant, ($event.target as HTMLSelectElement).value)"
            >
              <option v-if="tenant.tier.legacy" :value="tenant.tier.id">{{ tenant.tier.name }} · legacy</option>
              <option v-for="tier in activeTiers" :key="tier.id" :value="tier.id">{{ tier.name }}</option>
            </select>
            <span v-else class="tier-label">{{ tenant.tier.name }}<small v-if="tenant.tier.legacy">Legacy</small></span>
          </div>
          <span class="unavailable-value" role="cell" title="Probe inventory API not implemented">—</span>
          <div class="row-actions" role="cell">
            <button v-if="tenantManagementAllowed" class="icon-button" type="button" :disabled="busyTenantId === tenant.id" title="Rename tenant" @click="renameTenant(tenant)">
              <UserActionIcon name="rename" />
              <span class="visually-hidden">Rename {{ tenant.displayName }}</span>
            </button>
            <button v-if="tenantManagementAllowed" class="icon-button" type="button" title="Manage users" @click="openMembers(tenant)">
              <NavigationIcon name="users" />
              <span class="visually-hidden">Manage users for {{ tenant.displayName }}</span>
            </button>
            <button v-if="tenantManagementAllowed" class="icon-button icon-button--danger" type="button" :disabled="busyTenantId === tenant.id" title="Delete tenant" @click="deleteSelectedTenant(tenant)">
              <span aria-hidden="true">×</span><span class="visually-hidden">Delete {{ tenant.displayName }}</span>
            </button>
            <button
              v-if="tenantManagementAllowed"
              class="icon-button"
              type="button"
              :disabled="busyTenantId === tenant.id"
              :title="`${tenant.status === 'disabled' ? 'Enable' : 'Disable'} tenant`"
              @click="toggleTenantStatus(tenant)"
            >
              <span class="power-symbol" aria-hidden="true">⏻</span>
              <span class="visually-hidden">
                {{ tenant.status === "disabled" ? "Enable" : "Disable" }} {{ tenant.displayName }}
              </span>
            </button>
          </div>
        </div>
        <p v-if="tenantStore.tenants.length === 0" class="empty-state">No tenants exist yet.</p>
      </div>
    </section>
  </AppShell>
</template>
